import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Step1SelectCarrier from './Step1SelectCarrier';
import Step2ServicesAWB, { ServiceGroup } from './Step2ServicesAWB';
import Step3ReviewSubmit from './Step3ReviewSubmit';
import { Carrier, Shipment } from '../types';
import SbButton from '@Components/Button';
import { Dayjs } from 'dayjs';
import { useLocation } from 'react-router-dom';

interface CreateInvoiceFlowProps {
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
  handleReset: () => void;
  selectedCarrier: Carrier | null;
  setSelectedCarrier: React.Dispatch<React.SetStateAction<Carrier | null>>;
  invoiceDate: Dayjs | null;
  setInvoiceDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  dueDate: Dayjs | null;
  setDueDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  taxation: string;
  setTaxation: React.Dispatch<React.SetStateAction<string>>;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
}

const steps = ['Select Carrier', 'Select Services & AWB', 'Review & Submit'];

const CreateInvoiceFlow: React.FC<CreateInvoiceFlowProps> = ({
  activeStep,
  handleNext,
  handleBack,
  handleReset,
  selectedCarrier,
  setSelectedCarrier,
  invoiceDate,
  setInvoiceDate,
  dueDate,
  setDueDate,
  taxation,
  setTaxation,
  setActiveStep,
}) => {
  const location = useLocation();
  const [services, setServices] = useState<ServiceGroup[]>([]);
  const [serviceProviderData, setServiceProviderData] = useState<Shipment[]>([]);

  const selectedAwbs = services.flatMap(s => s.awbs.filter(a => a.selected));

  const isNextDisabled =
    (activeStep === 0 && !selectedCarrier) ||
    (activeStep === 1 && selectedAwbs.length === 0) ||
    (activeStep === 2 && (!invoiceDate || !dueDate));

  useEffect(() => {
    if (activeStep === 0) {
      setServices([]);
    }
  }, [activeStep]);

  useEffect(() => {
    if (location?.state?.services && location?.state?.selectedCarrier) {
      setSelectedCarrier(location.state.selectedCarrier);
      setServices(location.state.services);
      setActiveStep(1);
    }
  }, [location]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Step1SelectCarrier
            selectedCarrier={selectedCarrier}
            setSelectedCarrier={setSelectedCarrier}
            setServiceProviderData={setServiceProviderData}
          />
        );
      case 1:
        return (
          <Step2ServicesAWB
            selectedCarrier={selectedCarrier}
            services={services}
            setServices={setServices}
            serviceProviderData={serviceProviderData}
          />
        );
      case 2:
        return (
          <Step3ReviewSubmit
            selectedCarrier={selectedCarrier}
            awbs={selectedAwbs}
            services={services}
            invoiceDate={invoiceDate}
            setInvoiceDate={setInvoiceDate}
            dueDate={dueDate}
            setDueDate={setDueDate}
            taxation={taxation}
            setTaxation={setTaxation}
            handleReset={handleReset}
            setServiceProviderData={setServiceProviderData}
            setServices={setServices}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {renderStepContent(activeStep)}

      <Box
        sx={{
          mt: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          pt: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            width: { xs: '100%', sm: '40%', md: '30%' },
            maxWidth: 400,
            justifyContent: 'flex-end',
          }}
        >
          {/* {activeStep > 0 && ( */}
          <SbButton
            fullWidth
            variant="outlined"
            className="rounded-full"
            onClick={handleBack}
            sx={{ maxWidth: '200px' }}
          >
            Back
          </SbButton>
          {/* // )} */}

          {activeStep < steps.length - 1 && (
            <SbButton
              fullWidth
              variant="contained"
              className="rounded-full"
              onClick={handleNext}
              disabled={isNextDisabled}
            >
              Next
            </SbButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CreateInvoiceFlow;
